import React, { Fragment, useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getFAQById, deleteFAQ } from '../../actions/faq';
import { 
  ArrowLeft,
  Edit, 
  Trash2, 
  HelpCircle,
  CheckCircle,
  XCircle,
  Clock,
  Tag,
  AlertTriangle,
  Plus,
  Minus
} from 'lucide-react';

const FAQ = ({ 
  getFAQById, 
  deleteFAQ, 
  faq: { faq, loading }
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    document.title = 'FAQ Details';
    if (id) {
      getFAQById(id);
    }
  }, [getFAQById, id]);

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (id) {
      deleteFAQ(id);
      setShowDeleteModal(false);
      navigate('/inventory/v1.2/faqs');
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  const toggleCategory = (categoryIndex) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryIndex]: !prev[categoryIndex]
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getStatusBadge = (item) => {
    if (item.active) {
      return <span className="badge bg-success">Active</span>;
    } else {
      return <span className="badge bg-secondary">Inactive</span>;
    }
  };

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!faq) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-warning">
          <h4>FAQ Not Found</h4>
          <p>The requested FAQ could not be found.</p>
          <Link to="/inventory/v1.2/faqs" className="btn btn-primary">
            <ArrowLeft size={16} className="me-2" />
            Back to FAQs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <div className="container-fluid py-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <Link 
              to="/inventory/v1.2/faqs" 
              className="btn btn-outline-secondary me-3 d-flex align-items-center"
            >
              <ArrowLeft size={18} className="me-2" />
              Back
            </Link>
            <div>
              <h1 className="h3 mb-0 text-gray-800 d-flex align-items-center">
                <HelpCircle size={28} className="me-3 text-primary" />
                FAQ Details
              </h1>
              <p className="text-muted">
                View and manage FAQ content
              </p>
            </div>
          </div>
          <div className="d-flex gap-2">
            <Link
              to={`/inventory/v1.2/faqs/${id}/edit`}
              className="btn btn-primary d-flex align-items-center"
            >
              <Edit size={18} className="me-2" />
              Edit FAQ
            </Link>
            <button
              onClick={handleDeleteClick}
              className="btn btn-outline-danger d-flex align-items-center"
            >
              <Trash2 size={18} className="me-2" />
              Delete
            </button>
          </div>
        </div>

        {/* FAQ Info Card */}
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-light">
            <h5 className="mb-0 d-flex align-items-center">
              <HelpCircle size={20} className="me-2 text-primary" />
              FAQ Information
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <p className="text-muted mb-1">Status:</p>
                  {getStatusBadge(faq)}
                </div>
                <div className="mb-3">
                  <p className="text-muted mb-1">Created:</p>
                  <p className="fw-bold mb-0 d-flex align-items-center">
                    <Clock size={14} className="me-1" />
                    {formatDate(faq.createdAt)}
                  </p>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <p className="text-muted mb-1">Total Categories:</p>
                  <p className="fw-bold mb-0">{faq.category?.length || 0}</p>
                </div>
                <div className="mb-3">
                  <p className="text-muted mb-1">Last Updated:</p>
                  <p className="fw-bold mb-0 d-flex align-items-center">
                    <Clock size={14} className="me-1" />
                    {formatDate(faq.updatedAt)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Categories and FAQs */}
        <div className="card shadow-sm">
          <div className="card-header bg-light">
            <h5 className="mb-0 d-flex align-items-center">
              <Tag size={20} className="me-2 text-primary" />
              Categories & Questions
            </h5>
          </div>
          <div className="card-body">
            {faq.category && faq.category.length > 0 ? (
              <div className="accordion" id="faqAccordion">
                {faq.category.map((category, categoryIndex) => (
                  <div key={categoryIndex} className="accordion-item">
                    <h2 className="accordion-header" id={`heading${categoryIndex}`}>
                      <button
                        className="accordion-button"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#collapse${categoryIndex}`}
                        aria-expanded={expandedCategories[categoryIndex] ? 'true' : 'false'}
                        aria-controls={`collapse${categoryIndex}`}
                        onClick={() => toggleCategory(categoryIndex)}
                      >
                        <div className="d-flex align-items-center w-100">
                          <Tag size={16} className="me-2 text-primary" />
                          <span className="fw-bold">{category.name}</span>
                          <span className="badge bg-light text-dark ms-auto me-3">
                            {category.faqs?.length || 0} questions
                          </span>
                          {expandedCategories[categoryIndex] ? (
                            <Minus size={16} className="text-muted" />
                          ) : (
                            <Plus size={16} className="text-muted" />
                          )}
                        </div>
                      </button>
                    </h2>
                    <div
                      id={`collapse${categoryIndex}`}
                      className={`accordion-collapse collapse ${expandedCategories[categoryIndex] ? 'show' : ''}`}
                      aria-labelledby={`heading${categoryIndex}`}
                      data-bs-parent="#faqAccordion"
                    >
                      <div className="accordion-body">
                        {category.faqs && category.faqs.length > 0 ? (
                          <div className="row">
                            {category.faqs.map((faqItem, faqIndex) => (
                              <div key={faqIndex} className="col-12 mb-3">
                                <div className="card border-0 bg-light">
                                  <div className="card-body">
                                    <h6 className="card-title text-primary mb-2">
                                      <strong>Q: {faqItem.question}</strong>
                                    </h6>
                                    <p className="card-text mb-0">
                                      <strong>A:</strong> {faqItem.answer}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="alert alert-info">
                            <HelpCircle size={16} className="me-2" />
                            No questions in this category yet.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="alert alert-info">
                <HelpCircle size={16} className="me-2" />
                No categories found for this FAQ set.
              </div>
            )}
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header border-0">
                  <h5 className="modal-title d-flex align-items-center text-danger">
                    <AlertTriangle size={24} className="me-2" />
                    Confirm Delete
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={cancelDelete}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="text-center">
                    <div className="mb-3">
                      <Trash2 size={48} className="text-danger" />
                    </div>
                    <h6 className="mb-3">Delete FAQ Set?</h6>
                    <p className="text-muted mb-4">
                      This action cannot be undone. The FAQ set will be permanently deleted.
                    </p>
                    <div className="alert alert-warning d-flex align-items-center">
                      <AlertTriangle size={20} className="me-2" />
                      <small>
                        <strong>Warning:</strong> This will permanently delete this FAQ set and all its questions.
                      </small>
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={cancelDelete}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={confirmDelete}
                  >
                    <Trash2 size={16} className="me-2" />
                    Delete FAQ Set
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
};

FAQ.propTypes = {
  getFAQById: PropTypes.func.isRequired,
  deleteFAQ: PropTypes.func.isRequired,
  faq: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
  faq: state.faq
});

export default connect(
  mapStateToProps,
  { getFAQById, deleteFAQ }
)(FAQ);
